export type DurationVitalAttemptOutcome = 'success' | 'error' | 'abandoned';

interface DurationVitalAttempt {
    startedAt: number;
    context?: Record<string, unknown>;
}

export interface DurationVitalAttemptTracker {
    register: (instanceId: string, startedAt: number, context?: Record<string, unknown>) => void;
    updateContext: (instanceId: string, context?: Record<string, unknown>) => void;
    scheduleAbandon: (instanceId: string) => void;
    report: (
        instanceId: string,
        outcome: DurationVitalAttemptOutcome,
        reportContext?: Record<string, unknown>,
    ) => void;
    reportActive: (
        outcome: DurationVitalAttemptOutcome,
        reportContext?: Record<string, unknown>,
    ) => void;
    reset: () => void;
}

/**
 * Isolated attempt tracker for a duration vital: one emit per instance, deferred
 * abandon so StrictMode remount does not count as abandoned.
 */
export const createDurationVitalAttempt = (vitalName: string): DurationVitalAttemptTracker => {
    let activeInstanceId: string | null = null;
    const attemptsByInstanceId = new Map<string, DurationVitalAttempt>();
    const reportedKeys = new Set<string>();
    const pendingAbandonTimers = new Map<string, ReturnType<typeof setTimeout>>();

    const reportKeyFor = (instanceId: string) => `${vitalName}:${instanceId}`;

    const emit = (
        attempt: DurationVitalAttempt,
        outcome: DurationVitalAttemptOutcome,
        reportContext?: Record<string, unknown>,
    ) => {
        window.DD_RUM?.addDurationVital?.(vitalName, {
            startTime: attempt.startedAt,
            duration: Date.now() - attempt.startedAt,
            context: {
                ...attempt.context,
                ...reportContext,
                outcome,
            },
        });
    };

    const report = (
        instanceId: string,
        outcome: DurationVitalAttemptOutcome,
        reportContext?: Record<string, unknown>,
    ) => {
        const reportKey = reportKeyFor(instanceId);
        if (reportedKeys.has(reportKey)) {
            return;
        }

        const attempt = attemptsByInstanceId.get(instanceId);
        if (!attempt) {
            return;
        }

        const pendingAbandon = pendingAbandonTimers.get(instanceId);
        if (pendingAbandon) {
            clearTimeout(pendingAbandon);
            pendingAbandonTimers.delete(instanceId);
        }

        reportedKeys.add(reportKey);
        attemptsByInstanceId.delete(instanceId);

        if (activeInstanceId === instanceId) {
            activeInstanceId = null;
        }

        emit(attempt, outcome, reportContext);
    };

    const register = (instanceId: string, startedAt: number, context?: Record<string, unknown>) => {
        const pendingAbandon = pendingAbandonTimers.get(instanceId);
        if (pendingAbandon) {
            clearTimeout(pendingAbandon);
            pendingAbandonTimers.delete(instanceId);
        }

        activeInstanceId = instanceId;

        if (!attemptsByInstanceId.has(instanceId)) {
            attemptsByInstanceId.set(instanceId, { startedAt, context });
        }
    };

    const updateContext = (instanceId: string, context?: Record<string, unknown>) => {
        const attempt = attemptsByInstanceId.get(instanceId);
        if (!attempt) {
            return;
        }

        attempt.context = context;
    };

    const scheduleAbandon = (instanceId: string) => {
        if (reportedKeys.has(reportKeyFor(instanceId))) {
            return;
        }

        const existingTimer = pendingAbandonTimers.get(instanceId);
        if (existingTimer) {
            clearTimeout(existingTimer);
        }

        const timer = setTimeout(() => {
            pendingAbandonTimers.delete(instanceId);
            report(instanceId, 'abandoned');
        }, 0);

        pendingAbandonTimers.set(instanceId, timer);
    };

    const reportActive = (
        outcome: DurationVitalAttemptOutcome,
        reportContext?: Record<string, unknown>,
    ) => {
        if (!activeInstanceId) {
            return;
        }

        report(activeInstanceId, outcome, reportContext);
    };

    const reset = () => {
        activeInstanceId = null;
        attemptsByInstanceId.clear();
        reportedKeys.clear();
        pendingAbandonTimers.forEach(timer => clearTimeout(timer));
        pendingAbandonTimers.clear();
    };

    return { register, updateContext, scheduleAbandon, report, reportActive, reset };
};
